import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  Plus,
  Trash2,
  Copy,
  Check,
  RefreshCw,
  Shield,
  Users,
  Clock
} from "lucide-react";

interface Group {
  id: string;
  name: string;
  createdAt: string;
  createdBy: string;
  memberCount?: number;
  userRole?: 'admin' | 'member';
}

interface InviteCode {
  code: string;
  createdAt: string;
  createdBy: string;
  revoked: boolean;
  revokedAt?: string;
}

interface InviteManagementProps {
  group: Group;
  isOpen: boolean;
  onClose: () => void;
  onInviteGenerated?: () => void;
}

export function InviteManagement({ group, isOpen, onClose, onInviteGenerated }: InviteManagementProps) {
  const [inviteCodes, setInviteCodes] = useState<InviteCode[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isRevoking, setIsRevoking] = useState<string | null>(null);
  const [error, setError] = useState<string>("");
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchInviteCodes();
    }
  }, [isOpen, group.id]);

  const fetchInviteCodes = async () => {
    setIsLoading(true);
    setError("");

    try {
      const response = await fetch(`/api/groups/${group.id}/invites`);
      if (response.ok) {
        const data = await response.json();
        setInviteCodes(data.invites || []);
      } else {
        throw new Error("Failed to fetch invite codes");
      }
    } catch (error) {
      setError("Failed to load invite codes");
      console.error("Error fetching invite codes:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateInvite = async () => {
    setIsGenerating(true);
    setError("");

    try {
      const response = await fetch(`/api/groups/${group.id}/invites`, {
        method: "POST",
      });

      if (!response.ok) {
        throw new Error("Failed to generate invite code");
      }

      const result = await response.json();
      if (result.success && result.invite) {
        setInviteCodes(prev => [result.invite, ...prev]);
      } else {
        throw new Error("Invalid response format");
      }
      onInviteGenerated?.();
    } catch (error) {
      setError("Failed to generate invite code");
      console.error("Error generating invite:", error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRevokeInvite = async (code: string) => {
    setIsRevoking(code);
    setError("");

    try {
      const response = await fetch(`/api/groups/${group.id}/invites/${code}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        throw new Error("Failed to revoke invite code");
      }

      setInviteCodes(prev =>
        prev.map(invite =>
          invite.code === code
            ? { ...invite, revoked: true, revokedAt: new Date().toISOString() }
            : invite
        )
      );
    } catch (error) {
      setError("Failed to revoke invite code");
      console.error("Error revoking invite:", error);
    } finally {
      setIsRevoking(null);
    }
  };

  const handleCopyCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (error) {
      console.error("Error copying to clipboard:", error);
    }
  };

  const handleClose = () => {
    onClose();
    setError("");
    setCopiedCode(null);
  };

  const activeInvites = inviteCodes.filter(invite => !invite.revoked);
  const revokedInvites = inviteCodes.filter(invite => invite.revoked);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Manage Group Invites
          </DialogTitle>
          <DialogDescription>
            Generate and manage invite codes for "{group.name}"
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Generate New Invite */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Generate New Invite</CardTitle>
              <CardDescription>
                Create a new invite code that members can use to join your group
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                onClick={handleGenerateInvite}
                disabled={isGenerating}
                className="w-full"
              >
                {isGenerating ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4 mr-2" />
                    Generate Invite Code
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Active Invites */}
          {activeInvites.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Active Invites ({activeInvites.length})
                </CardTitle>
                <CardDescription>
                  These invite codes can be used to join your group
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {activeInvites.map((invite, index) => (
                    <div
                      key={invite.code || `active-invite-${index}`}
                      className="flex items-center justify-between p-3 rounded-lg border bg-card"
                    >
                      <div className="flex items-center gap-3">
                        <div className="font-mono text-sm bg-muted px-3 py-1 rounded">
                          {invite.code}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Created {new Date(invite.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleCopyCode(invite.code)}
                          className="flex items-center gap-1"
                        >
                          {copiedCode === invite.code ? (
                            <>
                              <Check className="h-3 w-3 text-green-600" />
                              Copied!
                            </>
                          ) : (
                            <>
                              <Copy className="h-3 w-3" />
                              Copy
                            </>
                          )}
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleRevokeInvite(invite.code)}
                          disabled={isRevoking === invite.code}
                          className="flex items-center gap-1"
                        >
                          {isRevoking === invite.code ? (
                            <>
                              <RefreshCw className="h-3 w-3 animate-spin" />
                              Revoking...
                            </>
                          ) : (
                            <>
                              <Trash2 className="h-3 w-3" />
                              Revoke
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Revoked Invites */}
          {revokedInvites.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Revoked Invites ({revokedInvites.length})
                </CardTitle>
                <CardDescription>
                  These invite codes are no longer valid
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {revokedInvites.map((invite, index) => (
                    <div
                      key={invite.code || `revoked-invite-${index}`}
                      className="flex items-center justify-between p-3 rounded-lg border bg-muted/50"
                    >
                      <div className="flex items-center gap-3">
                        <div className="font-mono text-sm text-muted-foreground px-3 py-1 rounded line-through">
                          {invite.code}
                        </div>
                        <Badge variant="secondary">Revoked</Badge>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        Revoked {invite.revokedAt ? new Date(invite.revokedAt).toLocaleDateString() : 'Recently'}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Empty State */}
          {activeInvites.length === 0 && revokedInvites.length === 0 && !isLoading && (
            <Card>
              <CardContent className="text-center py-8">
                <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">No Invite Codes</h3>
                <p className="text-muted-foreground mb-4">
                  Generate your first invite code to let members join your group
                </p>
              </CardContent>
            </Card>
          )}

          {/* Error State */}
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Instructions */}
          <div className="text-sm text-muted-foreground space-y-2 bg-muted/50 p-4 rounded-lg">
            <p><strong>How invite codes work:</strong></p>
            <ul className="list-disc list-inside space-y-1">
              <li>Generate a code and share it with people you want to invite</li>
              <li>Anyone with the code can join your group</li>
              <li>Revoke codes anytime to prevent further use</li>
              <li>You can generate new codes after revoking old ones</li>
            </ul>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}